/**
 * BottomNav (iter85)
 *
 * iter84 → iter85 change: removed the bottom "Search" tile (a search
 * bar already lives on every page header, the bottom tile was wasted
 * real-estate). Replaced with TWO browse tiles so the customer can
 * directly land on the two new browse modes shipped in iter83:
 *
 *   • Catalog → ``/products`` (Browse by Products — Flipkart-style
 *     Group Categories grid)
 *   • Shops   → ``/food``     (Browse by Shops — Swiggy-style restaurant list)
 *
 * Layout stays at 5 tiles (Home / Catalog / Shops / Orders / Account)
 * which is the canonical mobile nav rhythm — each tile ~72-80px wide,
 * still comfortable on iPhone-mini at 320px width.
 */
import { Link, useLocation } from "react-router-dom";
import { Home, Package, Store, ScrollText, User } from "lucide-react";
import { useTenant } from "../contexts/TenantContext";
import { t } from "../lib/i18n";

/** The customer's five destinations — shared with the desktop top bar (DesktopNav) so both stay identical. */
export const CUSTOMER_TABS = [
    { to: "/", icon: Home, key: "home" },
    { to: "/products", icon: Package, key: "catalog" },
    { to: "/food", icon: Store, key: "shops" },
    { to: "/orders", icon: ScrollText, key: "my_orders" },
    { to: "/profile", icon: User, key: "account" },
];

/** Which tab a path belongs to (same hierarchy rule as before — see the comment inside BottomNav). */
export function isCustomerTabActive(pathname, to) {
    if (to === "/") return pathname === "/";
    if (to === "/products") return pathname === "/products" || pathname.endsWith("/products");
    if (to === "/food") return pathname === "/food" || (pathname.startsWith("/c/") && !pathname.endsWith("/products"));
    return pathname === to || pathname.startsWith(to);
}

export default function BottomNav() {
    const { pathname } = useLocation();
    const { lang } = useTenant();
    const tabs = CUSTOMER_TABS;
    return (
        <nav
            data-testid="bottom-nav"
            // ``lg:hidden`` — from 1024px the customer shell shows DesktopNav (a top bar with these same tabs) instead.
            className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full z-30 bg-card border-t border-border flex items-center justify-around px-1 py-2 lg:hidden"
            style={{ maxWidth: "var(--shell-width)", paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
        >
            {tabs.map(({ to, icon: Icon, key }) => {
                // iter85 — `/c/:slug` and `/c/:slug/products` should
                // light up the right tile (Catalog for /products, Shops
                // for vertical shop browse). We do a hierarchy check:
                // Catalog tile owns ``/products`` + any ``…/products``
                // suffix; Shops tile owns ``/food`` + every ``/c/...``
                // path that isn't a /products variant.
                const active = isCustomerTabActive(pathname, to);
                return (
                    <Link
                        key={to}
                        to={to}
                        data-testid={`nav-${key}`}
                        className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition ${active ? "text-primary" : "text-muted-foreground"}`}
                    >
                        <Icon size={22} strokeWidth={active ? 2.5 : 2} />
                        <span className={`text-[11px] ${active ? "font-semibold" : "font-medium"}`}>{t(key, lang)}</span>
                    </Link>
                );
            })}
        </nav>
    );
}
